/**
 * Dashboard widget registry.
 *
 * Central store where widget types are registered and looked up.
 * Modules register their widgets at activation time; the dashboard
 * queries the registry to resolve widget components.
 *
 * Cross-References:
 *   - FR-DASH-003: Widget registration system
 *   - api-specifications/foundation.md §8: Dashboard widget types
 *   - dashboard screen spec (Artifact 2): Widget lifecycle
 */
import type { WidgetConfig } from '@/types';

/** Internal registry map: widget_type → config. */
const registry = new Map<string, WidgetConfig>();

/**
 * Register a widget type. Idempotent — re-registering the same type
 * overwrites the previous entry (useful for hot-reload in dev).
 */
export function registerWidget(config: WidgetConfig): void {
  registry.set(config.type, config);
}

/** Look up a widget by its type identifier. */
export function getWidget(type: string): WidgetConfig | undefined {
  return registry.get(type);
}

/** Return all registered widget configs (for the widget picker). */
export function listWidgets(): WidgetConfig[] {
  return Array.from(registry.values());
}

/** Remove a widget registration (e.g. when a module is uninstalled). */
export function unregisterWidget(type: string): boolean {
  return registry.delete(type);
}

/** Number of registered widget types. */
export function widgetCount(): number {
  return registry.size;
}

/* ── Auto-refresh hook (S8.08) ──────────────────────────────── */

import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * React hook that drives periodic data refresh for a widget.
 *
 * @param fetcher  Async function that loads the widget data.
 * @param intervalSeconds  Refresh period in seconds (0 = disabled).
 * @returns `{ data, loading, error, refresh }` — mirrors SWR semantics.
 *
 * The timer resets whenever `fetcher` or `intervalSeconds` change.
 * Calls to `refresh()` restart the timer from zero.
 *
 * Cross-References:
 *   - FR-DASH-004: Widget refresh intervals
 */
export function useAutoRefresh<T>(
  fetcher: () => Promise<T>,
  intervalSeconds: number = 0,
): {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const doFetch = useCallback(async () => {
    try {
      setLoading(prev => (data === null ? true : prev)); // only show spinner on first load
      const result = await fetcherRef.current();
      if (mountedRef.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []); // stable — uses ref internally

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (intervalSeconds > 0) {
      timerRef.current = setInterval(doFetch, intervalSeconds * 1000);
    }
  }, [intervalSeconds, doFetch, clearTimer]);

  /* Initial fetch + start timer */
  useEffect(() => {
    mountedRef.current = true;
    doFetch();
    startTimer();
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [doFetch, startTimer, clearTimer]);

  /* Manual refresh: re-fetch + restart timer */
  const refresh = useCallback(() => {
    doFetch();
    startTimer();
  }, [doFetch, startTimer]);

  return { data, loading, error, refresh };
}
